import React, { useState, useEffect, useRef } from "react";

/* ─── Design tokens ─── */
const NAVY    = "#1A2B4A";
const GOLD    = "#C8991A";
const CRIMSON = "#B22222";
const NAV_BG  = "#1E3760";

/* ================= MENU DATA ================= */
const menuData = [
  {
    title: "Administration",
    sections: [
      {
        heading: "General",
        items: [
          { title: "Principal", path: "/administrations/principal" },
          { title: "Governing Body", path: "/administrations/gb" },
          { title: "Former Principals", path: "/administrations/formerprincipals" },
          { title: "Office Staff", path: "/administrations/officestaff" },
        ],
      },
      {
        heading: "Others",
        items: [
          { title: "Committees", path: "/committees" },
          { title: "Organogram", path: "/administrations/organogram" },
        ],
      },
    ],
  },
  {
    title: "Academics",
    sections: [
      {
        heading: "Study",
        items: [
          { title: "Departments", path: "/departments" },
          { title: "Courses", path: "/syllabus-course" },
        ],
      },
      {
        heading: "Admission",
        items: [
          { title: "Procedure", path: "/admissionprocedure" },
          { title: "Helpdesk", path: "/admission-helpdesk" },
        ],
      },
    ],
  },
  {
    title: "Students",
    sections: [
      {
        heading: "Academic",
        items: [
          { title: "RCSU", path: "/students/students_union" },
          { title: "Results", path: "/students/results" },
          { title: "Exam Schedule", path: "/students/exam-schedule" },
          { title: "Routines", path: "/students/routines" },
          { title: "Anti-Ragging", path: "/facility/antiragging" },
          { title: "Apply for Certificate", path: "/students/certificates" },
        ],
      },
      {
        heading: "Activities",
        items: [
          { title: "NCC", path: "/ncc/home" },
          { title: "NSS", path: "/nss/home" },
          { title: "Scouts & Guides", path: "/facility/scoute_guides" },
          { title: "Red Cross", path: "/facility/red_cross" },
        ],
      },
    ],
  },
  {
    title: "Facilities",
    sections: [
      {
        heading: "Academic",
        items: [
          { title: "Laboratories", path: "/facility/research_laboratory" },
          { title: "Library", path: "/facility/library" },
          { title: "Conference Hall", path: "/facility/cfh" },
          { title: "Digital Learning Center", path: "/facility/ict_enabled_digital_classrooms" },
        ],
      },
      {
        heading: "Support",
        items: [
          { title: "Canteen", path: "/facility/canteen_facility" },
          { title: "Health Care", path: "/facility/healthcare_facility" },
          { title: "Counseling Cell", path: "/facility/Career_Placement_Cell" },
          { title: "Scholarships", path: "/facility/scholarship_and_stipen" },
          { title: "Scouts & Guides", path: "/facility/scoute_guides" },
        ],
      },
      {
        heading: "Infrastructure",
        items: [
          { title: "Hostel", path: "/facility/girlshostel" },
          { title: "Parking Facility", path: "/facility/parking_facility" },
          { title: "Sports", path: "/facility/sports_facility" },
          { title: "Disability Support", path: "/facility/disability_support" },
        ],
      },
    ],
  },
  { title: "IQAC", path: "/iqac/about" },
  {
    title: "Research",
    sections: [
      {
        heading: "Academic",
        items: [
          { title: "Research Publication", path: "/research" },
          { title: "Research Project", path: "/research-projects" },
          { title: "Research Journal", path: "/research-journal" },
          { title: "Research Notifications", path: "/research-notifications" },
        ],
      },
    ],
  },
  {
    title: "Publications",
    sections: [
      {
        heading: "Academic",
        items: [
          { title: "In-house Publications", path: "/publications" },
          { title: "Wall Magazine", path: "/wallmagazines" },
          { title: "NewsLetters", path: "/newsletter" },
        ],
      },
    ],
  },
  {
    title: "Alumni",
    sections: [
      {
        heading: "Network",
        items: [
          { title: "Alumni Association", path: "/alumni/committee" },
          { title: "Alumni Members", path: "/alumni/list" },
          { title: "Prominent Alumni", path: "/alumni/prominent" },
          { title: "Alumni Registration", path: "/alumni-registration" },
        ],
      },
      {
        heading: "Activities",
        items: [
          { title: "Alumni Support", path: "/alumni/support" },
          { title: "Alumni Events", path: "/alumni/events" },
        ],
      },
    ],
  },
  { title: "Grievances", path: "https://grievance.rangiacollege.ac.in/", external: true },
  { title: "Contact Us", path: "/contactus" },
];

/* ================= HELPERS ================= */
const hasSubmenu = (menu) => Array.isArray(menu?.sections) && menu.sections.length > 0;
const isExternalLink = (path = "") =>
  /^(https?:)?\/\//i.test(path) || path.startsWith("mailto:") || path.startsWith("tel:");

const HomeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 3 3 11h2v9h5v-6h4v6h5v-9h2z" />
  </svg>
);

const ChevronIcon = () => (
  <svg
    width="11"
    height="11"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    style={{ opacity: 0.7, marginLeft: 4 }}
  >
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

/* ================= COMPONENT ================= */
export default function MegaNavbar(props) {
  const [activeMenu, setActiveMenu] = useState(null);
  const navRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveMenu(null);
      }
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setActiveMenu(null);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const navigate = (path, options = {}) => {
    if (!path) return;
    if (options.external || isExternalLink(path)) {
      window.open(path, "_blank", "noopener,noreferrer");
      setActiveMenu(null);
      return;
    }
    if (props.history) props.history.push(path);
    else window.location.href = path;
    setActiveMenu(null);
  };

  const handleItemClick = (index, menu) => {
    if (hasSubmenu(menu)) {
      setActiveMenu(activeMenu === index ? null : index);
    } else {
      navigate(menu.path, { external: menu.external });
    }
  };

  return (
    <nav ref={navRef} className="rc-meganav">
      <div className="rc-meganav__inner">
        <button
          type="button"
          className="rc-meganav__btn"
          onClick={() => navigate("/")}
        >
          <HomeIcon />
          <span style={{ marginLeft: 6 }}>Home</span>
        </button>

        <span className="rc-meganav__sep" />

        {menuData.map((menu, index) => {
          const isActive = activeMenu === index;
          const submenu = hasSubmenu(menu);
          return (
            <div key={index} className="rc-meganav__item">
              <button
                type="button"
                className={`rc-meganav__btn${isActive ? " rc-meganav__btn--active" : ""}`}
                onClick={() => handleItemClick(index, menu)}
                aria-haspopup={submenu ? "true" : undefined}
                aria-expanded={submenu ? isActive : undefined}
              >
                <span>{menu.title}</span>
                {submenu && <ChevronIcon />}
              </button>

              {isActive && submenu && (
                <div className="rc-meganav__dropdown" role="menu">
                  <div className="rc-meganav__dropdown-accent" />
                  <div className="rc-meganav__dropdown-content">
                    <div className="rc-meganav__dropdown-title">{menu.title}</div>
                    <div className="rc-meganav__dropdown-grid">
                      {menu.sections.map((section, i) => (
                        <div key={i} className="rc-meganav__dropdown-col">
                          <div className="rc-meganav__dropdown-heading">
                            {section.heading}
                          </div>
                          {section.items.map((item, j) => (
                            <div
                              key={j}
                              role="menuitem"
                              tabIndex={0}
                              className="rc-meganav__dropdown-link"
                              onClick={() =>
                                navigate(item.path, { external: item.external })
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  navigate(item.path, { external: item.external });
                                }
                              }}
                            >
                              {item.title}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        .rc-meganav {
          background: linear-gradient(90deg, ${NAVY} 0%, ${NAV_BG} 100%);
          border-top: 3px solid ${GOLD};
          border-bottom: 1px solid rgba(200,153,26,0.25);
          position: relative;
          z-index: 100;
        }
        .rc-meganav__inner {
          display: flex;
          align-items: center;
          min-height: 48px;
          padding: 0 24px;
          flex-wrap: wrap;
        }
        .rc-meganav__item {
          position: relative;
        }
        .rc-meganav__btn {
          font-family: 'Lato', sans-serif;
          font-weight: 600;
          font-size: 0.78rem;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          color: rgba(255,255,255,0.88);
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          padding: 0 10px;
          height: 48px;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
          outline: none;
          transition: color 0.18s, border-color 0.18s, background 0.18s;
        }
        .rc-meganav__btn:hover,
        .rc-meganav__btn--active {
          color: ${GOLD};
          background: rgba(255,255,255,0.05);
          border-bottom-color: ${GOLD};
        }
        .rc-meganav__btn:focus-visible {
          outline: 2px solid ${GOLD};
          outline-offset: -2px;
        }
        .rc-meganav__sep {
          display: inline-block;
          width: 1px;
          height: 20px;
          background: rgba(200,153,26,0.35);
          margin: 0 4px;
        }
        .rc-meganav__dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          width: 780px;
          max-width: 96vw;
          display: flex;
          background: #fff;
          color: #374151;
          border: 1px solid rgba(200,153,26,0.2);
          border-top: 3px solid ${GOLD};
          border-radius: 0 0 8px 8px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.18);
          overflow: hidden;
          z-index: 1000;
        }
        .rc-meganav__dropdown-accent {
          width: 6px;
          flex-shrink: 0;
          background: linear-gradient(180deg, ${GOLD} 0%, ${CRIMSON} 100%);
        }
        .rc-meganav__dropdown-content {
          flex: 1;
          padding: 24px 28px 20px;
          min-width: 0;
        }
        .rc-meganav__dropdown-title {
          font-family: 'Playfair Display', Georgia, serif;
          font-size: 18px;
          font-weight: 700;
          color: ${NAVY};
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 2px solid ${GOLD};
          display: inline-block;
          min-width: 200px;
        }
        .rc-meganav__dropdown-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 32px;
        }
        .rc-meganav__dropdown-col {
          min-width: 0;
        }
        .rc-meganav__dropdown-heading {
          font-family: 'Lato', sans-serif;
          font-weight: 700;
          font-size: 11px;
          color: ${CRIMSON};
          letter-spacing: 1.4px;
          text-transform: uppercase;
          margin-bottom: 12px;
          padding-bottom: 6px;
          border-bottom: 1px solid #f0e8d4;
        }
        .rc-meganav__dropdown-link {
          cursor: pointer;
          font-family: 'Lato', sans-serif;
          font-size: 13.5px;
          margin-bottom: 6px;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 4px;
          transition: color 0.15s, padding-left 0.15s;
          user-select: none;
        }
        .rc-meganav__dropdown-link::before {
          content: "›";
          color: ${GOLD};
          font-weight: 700;
          font-size: 16px;
          line-height: 1;
        }
        .rc-meganav__dropdown-link:hover,
        .rc-meganav__dropdown-link:focus {
          color: ${CRIMSON};
          padding-left: 4px;
          outline: none;
        }
        @media (max-width: 900px) {
          .rc-meganav__dropdown {
            width: 92vw;
            left: 0;
          }
          .rc-meganav__dropdown-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>
    </nav>
  );
}
